import { useState, useEffect } from 'react';
import axios from 'axios';
import { Share2, Sparkles, Loader2, Check, X, ChevronDown, ChevronRight, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useProject } from '@/context/ProjectContext';
import { toast } from 'sonner';
import ProviderBadge from '@/components/ProviderBadge';
import ReportButton from '@/components/ReportButton';
import SocialPostCard from '@/components/SocialPostCard';
import { CONTENT_PILLARS } from '@/lib/sopConstants';
import { StaggerGroup, StaggerItem } from '@/components/motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_TABS = [
  { id: 'pending', label: 'New' },
  { id: 'approved', label: 'Approved' },
  { id: 'rejected', label: 'Rejected' },
];

const TONE_OPTIONS = [
  'Professional & Direct',
  'Warm & Approachable',
  'Bold & Confident',
  'Educational',
  'Playful',
  'Minimal & Understated',
];

const PLATFORM_OPTIONS = [
  'Instagram',
  'LinkedIn',
  'Facebook',
  'TikTok',
  'Twitter/X',
  'YouTube',
];

const FREQUENCY_OPTIONS = [
  'Daily',
  '3–4x per week',
  '1–2x per week',
  'Monthly',
];

const EMPTY_BRIEF = {
  tone: '',
  target_audience: '',
  primary_platforms: [],
  posting_frequency: '',
  content_focus: '',
  competitors_to_monitor: '',
};

const SocialStudioPanel = () => {
  const { currentProject, brandKit } = useProject();

  const [posts, setPosts] = useState([]);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [generating, setGenerating] = useState(false);
  const [fetching, setFetching] = useState(false);

  // BP-01-C brief
  const [briefOpen, setBriefOpen] = useState(false);
  const [socialBrief, setSocialBrief] = useState({ ...EMPTY_BRIEF });
  const [briefSaving, setBriefSaving] = useState(false);

  // Content pillar
  const [activePillar, setActivePillar] = useState(null);

  const setBriefField = (key, value) => setSocialBrief((prev) => ({ ...prev, [key]: value }));

  const togglePlatform = (platform) => {
    setSocialBrief((prev) => ({
      ...prev,
      primary_platforms: prev.primary_platforms.includes(platform)
        ? prev.primary_platforms.filter((p) => p !== platform)
        : [...prev.primary_platforms, platform],
    }));
  };

  const fetchPosts = async () => {
    if (!currentProject) return;
    try {
      setFetching(true);
      const res = await axios.get(`${API}/projects/${currentProject.id}/social`);
      setPosts(res.data);
    } catch (error) {
      console.error('Failed to fetch social posts:', error);
    } finally {
      setFetching(false);
    }
  };

  const fetchSocialBrief = async () => {
    if (!currentProject) return;
    try {
      const res = await axios.get(`${API}/projects/${currentProject.id}/social-brief`);
      if (res.data) setSocialBrief({ ...EMPTY_BRIEF, ...res.data });
    } catch {
      // No brief saved yet
    }
  };

  const fetchTone = async () => {
    if (!currentProject) return;
    try {
      const res = await axios.get(`${API}/projects/${currentProject.id}/tone`);
      if (res.data?.active_pillar) setActivePillar(res.data.active_pillar);
    } catch {
      // Tone not set yet
    }
  };

  useEffect(() => {
    fetchPosts();
    fetchSocialBrief();
    fetchTone();
  }, [currentProject?.id]);

  const handleSaveBrief = async () => {
    try {
      setBriefSaving(true);
      await axios.post(`${API}/projects/${currentProject.id}/social-brief`, socialBrief);
      toast.success('Social brief saved');
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || 'Failed to save social brief');
    } finally {
      setBriefSaving(false);
    }
  };

  const handlePillarSelect = async (pillarId) => {
    setActivePillar(pillarId);
    try {
      await axios.patch(`${API}/projects/${currentProject.id}/tone`, { active_pillar: pillarId });
    } catch {
      // Non-blocking — UI already updated optimistically
    }
  };

  const handleGenerate = async () => {
    try {
      setGenerating(true);
      const res = await axios.post(`${API}/projects/${currentProject.id}/social/generate`);
      setPosts((prev) => [...res.data, ...prev]);
      setStatusFilter('pending');
      toast.success(`Generated ${res.data.length} post${res.data.length !== 1 ? 's' : ''}`);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || 'Failed to generate posts');
    } finally {
      setGenerating(false);
    }
  };

  const updateStatus = async (postId, status) => {
    try {
      const res = await axios.patch(`${API}/projects/${currentProject.id}/social/${postId}`, { status });
      setPosts((prev) => prev.map((p) => (p.id === postId ? res.data : p)));
    } catch (error) {
      toast.error('Failed to update post');
    }
  };

  const visiblePosts = posts.filter((p) => p.status === statusFilter);
  const selectedPillarObj = CONTENT_PILLARS.find((p) => p.id === activePillar);

  if (!currentProject) return null;

  return (
    <div className="h-full overflow-auto p-6" data-testid="social-studio-panel">
      <div className="max-w-5xl mx-auto">
        <div className="mb-8">
          <div className="mb-6 flex items-start justify-between gap-4">
            <div>
              <h2 className="font-heading text-2xl text-foreground mb-2">Social Studio</h2>
              <p className="text-muted-foreground">Facebook &amp; Instagram post concepts, drafted straight from your Brand Kit and Strategy &mdash; nothing to type</p>
            </div>
            <ReportButton section="social" projectId={currentProject.id} />
          </div>

          <div className="glass rounded-2xl p-6 border border-border mb-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Share2 className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-heading text-lg text-foreground">Posts</h3>
                <p className="text-sm text-muted-foreground">2 Facebook + 2 Instagram concepts, each with real imagery</p>
              </div>
            </div>

            {/* BP-01-C: Social Brief (collapsible) */}
            <div className="border-t border-border pt-4 mb-4">
              <button
                type="button"
                onClick={() => setBriefOpen((o) => !o)}
                className="flex items-center gap-2 w-full text-left mb-1"
              >
                {briefOpen ? (
                  <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                )}
                <span className="font-mono text-xs uppercase tracking-wide lodge-accent">
                  Social Brief (BP-01-C)
                </span>
              </button>

              {briefOpen && (
                <div className="mt-3 space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-foreground/80">Tone</Label>
                      <select
                        value={socialBrief.tone}
                        onChange={(e) => setBriefField('tone', e.target.value)}
                        className="w-full rounded-md bg-black/[0.03] border border-border text-foreground px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary/50"
                      >
                        <option value="">— select —</option>
                        {TONE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>

                    <div className="space-y-1">
                      <Label className="text-foreground/80">Posting Frequency</Label>
                      <select
                        value={socialBrief.posting_frequency}
                        onChange={(e) => setBriefField('posting_frequency', e.target.value)}
                        className="w-full rounded-md bg-black/[0.03] border border-border text-foreground px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary/50"
                      >
                        <option value="">— select —</option>
                        {FREQUENCY_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
                      </select>
                    </div>

                    <div className="space-y-1 sm:col-span-2">
                      <Label className="text-foreground/80">Target Audience</Label>
                      <input
                        type="text"
                        value={socialBrief.target_audience}
                        onChange={(e) => setBriefField('target_audience', e.target.value)}
                        placeholder="e.g., Founders aged 28–42 building service businesses"
                        className="w-full rounded-md bg-black/[0.03] border border-border text-foreground placeholder:text-muted-foreground/70 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary/50"
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <Label className="text-foreground/80">Primary Platforms</Label>
                    <div className="flex flex-wrap gap-2">
                      {PLATFORM_OPTIONS.map((p) => (
                        <label key={p} className="flex items-center gap-1.5 cursor-pointer select-none text-sm text-foreground">
                          <input
                            type="checkbox"
                            checked={socialBrief.primary_platforms.includes(p)}
                            onChange={() => togglePlatform(p)}
                            className="accent-primary"
                          />
                          {p}
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-1">
                    <Label className="text-foreground/80">Content Focus</Label>
                    <textarea
                      value={socialBrief.content_focus}
                      onChange={(e) => setBriefField('content_focus', e.target.value)}
                      placeholder="What topics or themes should dominate the feed?"
                      rows={2}
                      className="w-full rounded-md bg-black/[0.03] border border-border text-foreground placeholder:text-muted-foreground/70 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary/50"
                    />
                  </div>

                  <div className="space-y-1">
                    <Label className="text-foreground/80">Competitors to Monitor</Label>
                    <input
                      type="text"
                      value={socialBrief.competitors_to_monitor}
                      onChange={(e) => setBriefField('competitors_to_monitor', e.target.value)}
                      placeholder="e.g., DesignCo, BrandHouse AU"
                      className="w-full rounded-md bg-black/[0.03] border border-border text-foreground placeholder:text-muted-foreground/70 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary/50"
                    />
                  </div>

                  <Button
                    size="sm"
                    onClick={handleSaveBrief}
                    disabled={briefSaving}
                    className="bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                  >
                    {briefSaving ? (
                      <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Saving…</>
                    ) : (
                      <><Save className="w-3.5 h-3.5 mr-1.5" />Save Brief</>
                    )}
                  </Button>
                </div>
              )}
            </div>

            {/* Content Pillar selector — always visible */}
            <div className="border-t border-border pt-4 mb-4">
              <p className="font-mono text-xs uppercase tracking-wide lodge-accent mb-2">Content Pillar</p>
              <div className="flex flex-wrap gap-2 mb-2">
                {CONTENT_PILLARS.map((pillar) => {
                  const isActive = activePillar === pillar.id;
                  return (
                    <button
                      key={pillar.id}
                      type="button"
                      onClick={() => handlePillarSelect(pillar.id)}
                      className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                        isActive
                          ? 'border-[#C5A059] text-[#C5A059]'
                          : 'bg-black/[0.03] text-muted-foreground border-border hover:text-foreground'
                      }`}
                      style={isActive ? { background: 'rgba(197,160,89,0.1)' } : {}}
                    >
                      {pillar.label}
                    </button>
                  );
                })}
              </div>
              {selectedPillarObj && (
                <p className="text-xs text-muted-foreground">{selectedPillarObj.description}</p>
              )}
            </div>

            <Button
              onClick={handleGenerate}
              disabled={generating}
              className="bg-primary text-primary-foreground font-semibold hover:bg-primary/90 disabled:opacity-50"
              data-testid="social-generate-btn"
            >
              {generating ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating posts &amp; imagery... (can take a few minutes)</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" />{posts.length > 0 ? 'Generate More' : 'Generate Posts'}</>
              )}
            </Button>
          </div>

          <div className="flex items-center gap-2 mb-4">
            {STATUS_TABS.map((tab) => {
              const count = posts.filter((p) => p.status === tab.id).length;
              return (
                <button
                  key={tab.id}
                  onClick={() => setStatusFilter(tab.id)}
                  className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                    statusFilter === tab.id
                      ? 'bg-primary/10 text-primary border-primary/30'
                      : 'bg-black/[0.03] text-muted-foreground border-border hover:text-foreground'
                  }`}
                  data-testid={`social-status-tab-${tab.id}`}
                >
                  {tab.label} {count > 0 && <span className="opacity-70">({count})</span>}
                </button>
              );
            })}
          </div>

          {fetching && posts.length === 0 ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 text-primary animate-spin" />
            </div>
          ) : visiblePosts.length === 0 ? (
            <div className="glass rounded-2xl p-10 border border-border text-center">
              <Share2 className="w-10 h-10 text-muted-foreground/50 mx-auto mb-3" />
              <p className="text-muted-foreground">
                {posts.length === 0 ? 'No posts yet. Generate some above.' : `No ${statusFilter} posts.`}
              </p>
            </div>
          ) : (
            <StaggerGroup className="grid grid-cols-1 md:grid-cols-2 gap-6" stagger={0.08}>
              {visiblePosts.map((post) => (
                <StaggerItem key={post.id} className="flex flex-col items-center gap-3">
                  <SocialPostCard post={post} brandKit={brandKit} businessName={currentProject.name} />
                  <div className="flex items-center gap-2 w-full max-w-sm">
                    <ProviderBadge provider={post.generated_by} />
                    {post.status !== 'approved' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateStatus(post.id, 'approved')}
                        className="ml-auto bg-black/[0.03] border-border text-foreground hover:bg-black/[0.06]"
                        data-testid={`social-approve-${post.id}`}
                      >
                        <Check className="w-3.5 h-3.5 mr-1.5" />Approve
                      </Button>
                    )}
                    {post.status !== 'rejected' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateStatus(post.id, 'rejected')}
                        className="bg-black/[0.03] border-border text-foreground hover:bg-black/[0.06]"
                        data-testid={`social-reject-${post.id}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </StaggerItem>
              ))}
            </StaggerGroup>
          )}
        </div>
      </div>
    </div>
  );
};

export default SocialStudioPanel;
